import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import { ohlasUpozorneniaHran } from "@/lib/stavHran";
import {
  najdiDielec,
  popisDielca,
  predchadzajuceHodnoty,
} from "@/lib/undo/dielecUndo";
import type {
  DataResponse,
  Dielec,
  DielecCreate,
  DielecUpdate,
  ZakazkaDetail,
} from "@/api/schemas";

export function useCreateDielec(
  zakazkaId: string,
  zostavaId: string,
): UseMutationResult<DataResponse<Dielec>, Error, DielecCreate> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (payload: DielecCreate) => {
      const { data } = await api.post<DataResponse<Dielec>>(
        `/zostavy/${zostavaId}/dielce`,
        payload,
      );
      return data;
    },
    onSuccess: (vysledok) => {
      // Predvoľba hrán názvu vie hrúbku nahradiť (KOLO 135) — povedz to
      ohlasUpozorneniaHran(vysledok.data);
      undo?.pridaj({
        popis: `pridanie dielca ${vysledok.data.kod ?? ""}`.trim(),
        vrat: async () => {
          await api.delete(`/dielce/${vysledok.data.id}`);
          await invaliduj(qc, zakazkaId);
        },
      });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      // Pravé súhrny hrán/kovaní žijú pod varianty.all — bez tohto sa
      // nové kovanie/hrana objaví v prehľade až po F5.
      qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() });
    },
  });
}

/**
 * Create dielec s dynamickou zostavou — pre quick-add a duplikovanie
 * priamo z tabuľky (zostavaId je súčasť mutácie, nie hooku).
 */
export function useQuickCreateDielec(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<Dielec>,
  Error,
  { zostavaId: string; data: DielecCreate }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ zostavaId, data: payload }) => {
      const { data } = await api.post<DataResponse<Dielec>>(
        `/zostavy/${zostavaId}/dielce`,
        payload,
      );
      return data;
    },
    onSuccess: (vysledok) => {
      // Predvoľba hrán názvu vie hrúbku nahradiť (KOLO 135) — povedz to
      ohlasUpozorneniaHran(vysledok.data);
      undo?.pridaj({
        popis: `pridanie dielca ${vysledok.data.kod ?? ""}`.trim(),
        vrat: async () => {
          await api.delete(`/dielce/${vysledok.data.id}`);
          await invaliduj(qc, zakazkaId);
        },
      });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() });
    },
  });
}

/**
 * PATCH dielca s **optimistic update** (DATA_FLOW.md §"Optimistic updates").
 *
 * Workflow:
 * 1. onMutate: cancel inprogress fetchov, snapshot pôvodného stavu,
 *    optimistická úprava ZakazkaDetail v cache (UI ukáže novú hodnotu hneď).
 * 2. mutationFn: PATCH /api/dielce/{id} — backend prepočíta a vráti dielec
 *    s aktuálnou cena_dielca.
 * 3. onError: rollback z context.previous (UI sa vráti).
 * 4. onSettled: invalidate detail query → re-fetch, server vie najlepšie
 *    (vrátane novej cena_dielca, cena_zostavy, cena_varianta).
 */
export function useUpdateDielec(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<Dielec>,
  Error,
  { id: string; data: DielecUpdate },
  { previous: DataResponse<ZakazkaDetail> | undefined }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<Dielec>>(
        `/dielce/${id}`,
        data,
      );
      return response.data;
    },
    onMutate: async ({ id, data }) => {
      const key = queryKeys.zakazky.detail(zakazkaId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<DataResponse<ZakazkaDetail>>(key);
      if (previous) {
        qc.setQueryData<DataResponse<ZakazkaDetail>>(key, (old) => {
          if (!old) return old;
          return {
            data: applyDielecChanges(old.data, id, data),
          };
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(
          queryKeys.zakazky.detail(zakazkaId),
          context.previous,
        );
      }
    },
    onSuccess: (vysledok, { id, data }, context) => {
      // Hrany po zmene hrúbky: čo appka vymenila alebo nechala (KOLO 135)
      ohlasUpozorneniaHran(vysledok.data);
      // Predchádzajúce hodnoty berieme zo snapshotu, ktorý `onMutate`
      // spravil kvôli optimistickému updatu — undo tak nič nedopytuje
      // a nemôže sa rozísť s tým, čo user videl.
      const puvodny = najdiDielec(context?.previous?.data, id);
      const naspat = predchadzajuceHodnoty(puvodny, data);
      if (Object.keys(naspat).length === 0) return;
      undo?.pridaj({
        popis: `zmenu dielca ${popisDielca(puvodny)}`,
        vrat: async () => {
          await api.patch(`/dielce/${id}`, naspat);
          await invaliduj(qc, zakazkaId);
        },
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      // Zmena hrán / slotu dielca mení sumár hrán vo variante
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() });
    },
  });
}

export function useDeleteDielec(
  zakazkaId: string,
): UseMutationResult<void, Error, string, { kod: string }> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/dielce/${id}`);
    },
    onMutate: (id) => {
      // Kód sa musí prečítať **pred** zmazaním — po ňom dielec z cache
      // vypadne a popis kroku by ostal prázdny.
      const detail = qc.getQueryData<DataResponse<ZakazkaDetail>>(
        queryKeys.zakazky.detail(zakazkaId),
      );
      return { kod: popisDielca(najdiDielec(detail?.data, id)) };
    },
    onSuccess: (_data, id, context) => {
      undo?.pridaj({
        popis: `zmazanie dielca ${context?.kod ?? ""}`.trim(),
        vrat: async () => {
          await api.post(`/dielce/${id}/obnovit`);
          await invaliduj(qc, zakazkaId);
        },
      });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() });
    },
  });
}

/**
 * Prepočet po vrátení — zákazka + súhrny hrán a kovaní.
 *
 * Vrátenie beží mimo mutácií, takže si invalidáciu musí spraviť samo;
 * `useBulkDielce.ts` má rovnaký zoznam kľúčov.
 */
async function invaliduj(
  qc: ReturnType<typeof useQueryClient>,
  zakazkaId: string,
): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) }),
    qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() }),
    qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() }),
  ]);
}

/**
 * Helper: aplikuj zmeny na dielec vo vnorenej ZakazkaDetail štruktúre.
 *
 * Hľadá `dielec_id` cez varianty → zostavy → dielce a vráti **nový**
 * `ZakazkaDetail` objekt (immutable update pre React re-render).
 *
 * Final cast cez `as ZakazkaDetail` obchádza openapi-typescript šum
 * (Dielec.kod má v gen-types `string | null` aj keď backend ho má vždy
 * required). Bez castu by sa všetky `kod` polia mali narazom validovať.
 */
function applyDielecChanges(
  zakazka: ZakazkaDetail,
  dielecId: string,
  changes: DielecUpdate,
): ZakazkaDetail {
  const varianty = (zakazka.varianty ?? []).map((v) => ({
    ...v,
    zostavy: (v.zostavy ?? []).map((z) => ({
      ...z,
      dielce: (z.dielce ?? []).map((d) =>
        d.id === dielecId ? { ...d, ...changes } : d,
      ),
    })),
  }));
  return { ...zakazka, varianty } as ZakazkaDetail;
}
